import React, { useEffect, useRef, useState } from 'react';
import { Button, Select, Tooltip, Dropdown, Input, message } from 'antd';
import { PlayCircleOutlined, MoreOutlined } from '@ant-design/icons';
import { BarChart3, Check, LoaderCircle, Pencil, Settings, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { VERDICTIA_MARK_SRC } from "../../../brand/verdictiaBrand";
import LanguageMenu from "../../common/header/LanguageSelect";
import ProfileSettingsModal from "../../common/header/ProfileSettingsModal";
import AddVersionButton from "../document/AddVersionButton";
import { FlushAnalyseButton } from "../document/FlushAnalyseButton";
import { usePageTransition } from "../../../utils/pageTransition";
import { useDispatch, useSelector } from "react-redux";
import { flushAnalyseCriteria, toggleCriterionSelection } from "../../../Actions/stage.action";
import { RequestUpdateVersionTitle } from "../../../Actions/documents.action";
import { UserActionTypes } from "../../../Actions/actionTypes";
import { update_current_user_profile } from "../../../Services/user.service";
import { fetchCommercialUsage, openCommercialGateModal } from "../../../Actions/commercialControl.action";
import UsageAccessButton from "../commercial/UsageAccessButton";
import UsagePanel from "../commercial/UsagePanel";
import { buildLockedCommercialDecision } from "../../../utils/commercialCapabilities";
import { findGlobalAnalysisCriterion } from "../../../utils/globalAnalysisCriterion";
import { hasCompletePov } from "../../../utils/pov";
import PovScopeNotice from "../comments/PovScopeNotice";
import './reviewHeader.css'

const { Option } = Select;

const formatDate = (dateString) => {
    const date = new Date(dateString);
    const userTimezoneOffset = date.getTimezoneOffset() * 60000;
    const localDate = new Date(date.getTime() - userTimezoneOffset);

    return localDate.toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
    });
};

const normalizeTitle = (value) => String(value || "").trim();

const EditableHeaderTitle = ({ title, disabled, onSave }) => {
    const { t } = useTranslation('review');
    const [isEditing, setIsEditing] = useState(false);
    const [draft, setDraft] = useState(title || "");
    const [isSaving, setIsSaving] = useState(false);
    const editorRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        if (!isEditing) {
            setDraft(title || "");
        }
    }, [title, isEditing]);

    useEffect(() => {
        if (isEditing) {
            window.setTimeout(() => inputRef.current?.focus?.({ cursor: "all" }), 0);
        }
    }, [isEditing]);

    const startEditing = () => {
        if (disabled || isSaving) return;
        setDraft(title || "");
        setIsEditing(true);
    };

    const cancelEditing = () => {
        if (isSaving) return;
        setDraft(title || "");
        setIsEditing(false);
    };

    const saveTitle = async () => {
        if (isSaving) return;

        const nextTitle = normalizeTitle(draft);
        if (!nextTitle) {
            message.warning(t('title_edit.empty'));
            inputRef.current?.focus?.();
            return;
        }

        if (nextTitle === normalizeTitle(title)) {
            setDraft(title || "");
            setIsEditing(false);
            return;
        }

        setIsSaving(true);
        try {
            await onSave(nextTitle);
            setDraft(nextTitle);
            setIsEditing(false);
            message.success(t('title_edit.saved'));
        } catch (error) {
            message.error(
                error?.response?.data?.message ||
                error?.response?.data?.detail ||
                t('title_edit.error')
            );
        } finally {
            setIsSaving(false);
        }
    };

    const handleEditorBlur = (event) => {
        if (editorRef.current?.contains(event.relatedTarget)) return;
        saveTitle();
    };

    if (isEditing) {
        return (
            <div
                ref={editorRef}
                className="review-title-editor"
                onBlur={handleEditorBlur}
            >
                <Input
                    ref={inputRef}
                    className="review-title-input"
                    value={draft}
                    maxLength={255}
                    disabled={isSaving}
                    onChange={(event) => setDraft(event.target.value)}
                    onKeyDown={(event) => {
                        if (event.key === "Enter") saveTitle();
                        if (event.key === "Escape") cancelEditing();
                    }}
                    aria-label={t('title_edit.aria_label')}
                />
                <Tooltip title={t('title_edit.save')}>
                    <Button
                        className="review-title-icon-btn review-title-icon-btn--save"
                        type="text"
                        disabled={isSaving}
                        icon={isSaving ? <LoaderCircle size={14} className="review-title-spin" /> : <Check size={14} />}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={saveTitle}
                        aria-label={t('title_edit.save')}
                    />
                </Tooltip>
                <Tooltip title={t('title_edit.cancel')}>
                    <Button
                        className="review-title-icon-btn"
                        type="text"
                        disabled={isSaving}
                        icon={<X size={14} />}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={cancelEditing}
                        aria-label={t('title_edit.cancel')}
                    />
                </Tooltip>
            </div>
        );
    }

    return (
        <div className="review-title-inline" onDoubleClick={startEditing}>
            <Tooltip title={title} placement="bottomLeft">
                <h1 className="review-header-title">{title}</h1>
            </Tooltip>
            {!disabled && (
                <Tooltip title={t('title_edit.edit')}>
                    <Button
                        className="review-title-edit-btn"
                        type="text"
                        icon={<Pencil size={13} />}
                        onClick={startEditing}
                        aria-label={t('title_edit.edit')}
                    />
                </Tooltip>
            )}
        </div>
    );
};

const ReviewHeader = ({
                          isCreate,
                          headerTitle,
                          stage,
                          criteriaListe,
                          currentVersion,
                          onVersionChange,
                          onLaunchAnalysis,
                          isAnalysisRunning,
                          isGlobalLoading,
                          isProcessing,
                          isLoading,
                           analyseId,
                           isMetadataIncompleteForAnalysis,
                           isCommercialChecking = false,
                           commentsCount = 0,
                           commentsLoading = false,
                           hasCompleteAnalysisPov,
                           isPovTransitioning = false,
                           povTransitionPartyName = null,
                           onPovRequired,
                      }) => {
    const { t } = useTranslation('review');
    const { navigateWithFade } = usePageTransition();
    const { t: tUsers } = useTranslation('users');
    const dispatch = useDispatch();
    const userState = useSelector((state) => state.users);
    const commercialControl = useSelector((state) => state.commercialControl);
    const currentUser = userState.user?.id ? userState.user : userState.currentUser;
    const isSuperAdmin = String(currentUser?.role || "").toUpperCase() === "SUPER_ADMIN";
    const canManageBilling = ["SUPER_ADMIN", "ADMIN_CLIENT"].includes(currentUser?.role);
    const billingReturnPath = currentUser?.role === "SUPER_ADMIN"
        ? "/admin/commercial?tab=subscriptions"
        : "/workspace";
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [usagePanelOpen, setUsagePanelOpen] = useState(false);
    const [savingSettings, setSavingSettings] = useState(false);

    const globalCriteria = findGlobalAnalysisCriterion(criteriaListe || []);

    const selectedCriteria = stage?.selectedCriteria || [];
    // Robuste: supporte un tableau d'objets {id} ou un tableau d'ids
    const selectedCriteriaIds = selectedCriteria.map(c => (typeof c === 'object' ? c.id : c));

    const globalId = globalCriteria?.id;
    const isGlobalExecuted = globalCriteria?.status === "completed";

    // Si des critères sont déjà complétés dans la liste (par ex. après une analyse),
    // permettre le flush même s'il n'y a pas de sélection active.
    const hasCompletedCriteria = (stage?.criteriaListe || []).some(c => c.status === 'completed');

    const nonGlobalSelectedIds = selectedCriteriaIds.filter(id => id !== globalId);
    const hasNonGlobalSelection = nonGlobalSelectedIds.length > 0;
    const completeAnalysisPov = hasCompleteAnalysisPov
        ?? hasCompletePov(stage?.main_document);

    const canLaunchBusinessRule =
        (!hasNonGlobalSelection && !isGlobalExecuted) ||
        hasNonGlobalSelection;

    // IMPORTANT: ne pas désactiver le bouton juste parce qu'il n'y a aucune sélection.
    // S'il n'y a rien de sélectionné et que l'analyse globale n'est pas encore exécutée,
    // le clic explicite sur le CTA utilise l'analyse globale comme fallback.
    const isButtonDisabled =
        isProcessing ||
        isLoading ||
        isGlobalLoading ||
        isCommercialChecking ||
        isPovTransitioning ||
        (isMetadataIncompleteForAnalysis && completeAnalysisPov) ||
        (completeAnalysisPov && !canLaunchBusinessRule);

    const handleLaunchClick = () => {
        if (isPovTransitioning) return;
        if (!completeAnalysisPov) {
            onPovRequired?.();
            return;
        }

        const selectedCriteria = stage?.selectedCriteria || [];
        const hasSelection = selectedCriteria.length > 0;

        const globalCriteria = findGlobalAnalysisCriterion(criteriaListe || []);

        const isGlobalExecuted = globalCriteria?.status === "completed";

        if (!hasSelection && !isGlobalExecuted && globalCriteria?.id) {
            dispatch(toggleCriterionSelection(globalCriteria.id));
        }

        const launchAnalysis = () => {
            onLaunchAnalysis();
        };

        launchAnalysis();
    };

    const handleTitleSave = (nextTitle) => {
        return dispatch(RequestUpdateVersionTitle(currentVersion, nextTitle));
    };

    const refreshCommercialUsage = () => {
        const result = dispatch(fetchCommercialUsage());
        if (result?.catch) {
            result.catch(() => {});
        }
        return result;
    };

    const openUsagePanel = () => {
        setUsagePanelOpen(true);
        refreshCommercialUsage();
    };

    const openUsageUpgradeOptions = () => {
        dispatch(openCommercialGateModal(buildLockedCommercialDecision("complete_analysis", {
            code: "usage_panel_upgrade_prompt",
            titleFr: "Changer de plan",
            bodyFr: "Choisissez une offre supérieure pour augmenter les capacités de ce workspace.",
            titleEn: "Change plan",
            bodyEn: "Choose a higher plan to increase this workspace's capacity.",
            ctaFr: "Voir les options",
            ctaEn: "View options",
        })));
    };

    const handleSettingsSave = async (values) => {
        setSavingSettings(true);
        try {
            const result = await update_current_user_profile({
                audience_profile: values.audience_profile,
            });
            const updatedUser = result?.data;
            if (updatedUser) {
                dispatch({ type: UserActionTypes.SET_CURRENT_USER, payload: updatedUser });
                dispatch({ type: UserActionTypes.SET_USER, payload: updatedUser });
            }
            message.success(tUsers("profileSettings.success"));
            setSettingsOpen(false);
        } catch (err) {
            message.error(
                err?.response?.data?.detail ||
                err?.response?.data?.error ||
                err?.response?.data?.message ||
                tUsers("profileSettings.error")
            );
        } finally {
            setSavingSettings(false);
        }
    };

    const VersionSelect = ({ className }) => (
        <Select
            value={currentVersion}
            onChange={onVersionChange}
            className={className}
            placeholder={t('select_version')}
            optionLabelProp="label"
        >
            {stage?.versions?.map(version => (
                <Option 
                    key={version.id} 
                    value={version.id}
                    label={`${t('version')} ${version.version}`}
                >
                    <div className="review-version-option">
                        <span className="review-version-label">
                            {t('version')} {version.version}
                        </span>
                        <span className="review-version-date">
                            {formatDate(version.created_at)}
                        </span>
                    </div>
                </Option>
            ))}
        </Select>
    );

    const overflowMenuItems = [
        {
            key: 'version',
            label: (
                <div style={{ padding: '4px 0' }} onClick={(e) => e.stopPropagation()}>
                    <VersionSelect className="review-version-select-mobile" />
                </div>
            ),
        },
        {
            key: 'usage',
            icon: <BarChart3 size={15} strokeWidth={1.9} />,
            label: t('commercial.usage.title', { defaultValue: 'Usage du plan' }),
            onClick: openUsagePanel,
        },
        ...(currentUser?.id ? [{
            key: 'profile-settings',
            icon: <Settings size={15} strokeWidth={1.9} />,
            label: tUsers("profileSettings.title"),
            onClick: () => setSettingsOpen(true),
        }] : []),
    ];

    return (
        <>
            <header className="review-header">
                <div className="review-header-container">

                {/* ── Left: Logo + Title + Template ── */}
                <div className="review-header-left">
                    <button
                        type="button"
                        className="review-header-logo-wrap"
                        onClick={() => navigateWithFade("/workspace")}
                    >
                        <img
                            src={VERDICTIA_MARK_SRC}
                            alt="Verdictia"
                            className="review-header-logo"
                        />
                    </button>


                    <div className="review-header-title-wrap">
                        <EditableHeaderTitle
                            title={headerTitle || t('document')}
                            disabled={isCreate || !currentVersion}
                            onSave={handleTitleSave}
                        />
                    </div>

                    {!isCreate && (
                        <PovScopeNotice
                            partyRole={stage?.main_document?.party_role}
                            studiedPartyName={stage?.main_document?.studied_party_name}
                            commentsCount={commentsCount}
                            loading={commentsLoading}
                            transitioning={isPovTransitioning}
                            transitionPartyName={povTransitionPartyName}
                        />
                    )}
                </div>

                {/* ── Right: Actions ── */}
                    <div className="review-header-right">
                    {!isCreate && (
                        <>
                            {/* Version selector + Add version */}
                            <div className="review-header-version">
                                <VersionSelect className="review-version-select" />
                                <AddVersionButton analyseId={analyseId} />
                            </div>

                            <div className="review-header-divider" />

                            <UsageAccessButton
                                usageByType={commercialControl?.usageByType}
                                isLoading={commercialControl?.isLoadingUsage}
                                onOpen={openUsagePanel}
                            />

                            {/* Flush — secondary action */}
                            {isSuperAdmin && (
                                <FlushAnalyseButton
                                    className="review-header-flush"
                                    onConfirm={() => dispatch(flushAnalyseCriteria())}
                                    loading={false}
                                    // Opération destructive de support interne, réservée au SUPER_ADMIN.
                                    disabled={(selectedCriteriaIds.length === 0 && !hasCompletedCriteria)}
                                />
                            )}

                            {/* Launch — primary CTA */}
                            <Tooltip 
                                title={!completeAnalysisPov
                                    ? t('pov_scope.select')
                                    : isMetadataIncompleteForAnalysis
                                        ? t('metadata_required_for_analysis')
                                        : null}
                                placement="bottom"
                            >
                                <Button
                                    className="review-header-launch"
                                    disabled={isButtonDisabled}
                                    loading={isAnalysisRunning || isCommercialChecking}
                                    onClick={handleLaunchClick}
                                    type="primary"
                                    icon={<PlayCircleOutlined />}
                                >
                                    <span className="review-launch-text">{t('launch_analysis')}</span>
                                </Button>
                            </Tooltip>

                            {/* Mobile overflow */}
                            <Dropdown
                                menu={{ items: overflowMenuItems }}
                                placement="bottomRight"
                                trigger={['click']}
                            >
                                <Button
                                    className="review-header-more"
                                    icon={<MoreOutlined />}
                                    aria-label={t('more_actions', { defaultValue: "Plus d'actions" })}
                                />
                            </Dropdown>
                        </>
                    )}

                    {/* Language selector */}
                        <div className="review-header-lang">
                            <LanguageMenu theme="blue" />
                        </div>

                        {currentUser?.id && (
                            <Tooltip title={tUsers("profileSettings.title")} placement="bottomRight">
                                <Button
                                    type="text"
                                    className="review-header-profile-settings"
                                    icon={<Settings size={17} strokeWidth={1.9} />}
                                    onClick={() => setSettingsOpen(true)}
                                    aria-label={tUsers("profileSettings.title")}
                                />
                            </Tooltip>
                        )}
                    </div>

                </div>
            </header>

            <ProfileSettingsModal
                open={settingsOpen}
                currentUser={currentUser}
                saving={savingSettings}
                onCancel={() => setSettingsOpen(false)}
                onSubmit={handleSettingsSave}
                plan={commercialControl?.plan}
                usageByType={commercialControl?.usageByType}
                isUsageLoading={commercialControl?.isLoadingUsage}
                onOpenUsage={openUsagePanel}
            />

            <UsagePanel
                open={usagePanelOpen}
                onClose={() => setUsagePanelOpen(false)}
                usageSnapshot={commercialControl?.usageSnapshot}
                usageByType={commercialControl?.usageByType}
                capabilities={commercialControl?.capabilities}
                plan={commercialControl?.plan}
                isLoading={commercialControl?.isLoadingUsage}
                error={commercialControl?.lastCommercialError}
                onRefresh={refreshCommercialUsage}
                onUpgrade={openUsageUpgradeOptions}
                canManageBilling={canManageBilling}
                billingReturnPath={billingReturnPath}
            />
        </>
    );
};

export default ReviewHeader;
